import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import Chart from 'react-apexcharts';

import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function formatDate(value) {
  if (!value) return '';
  return String(value).substring(5, 10);
}

function formatNumber(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return `${(numberValue / 1000000).toFixed(1)}M`;
  }

  if (numberValue >= 1000) {
    return `${(numberValue / 1000).toFixed(1)}K`;
  }

  return numberValue.toFixed(0);
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

class PromotionsEvolution extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      data: [],
      metric: 'claims',
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.periodMode !== this.props.periodMode) {
      this.loadData();
    }
  }

  loadData = () => {
    const periodMode = this.props.periodMode || 'last_30d';

    let queryData = {
      periodMode: periodMode,
    };

    let data = BackofficeReportsApi.getPromotionsActivityEvolution(this.state.client, queryData);

    if (data) {
      let rows = [];

      if (Array.isArray(data)) {
        rows = data;
      } else if (Array.isArray(data.rows)) {
        rows = data.rows;
      }

      this.setState({
        data: rows,
      });
    } else {
      this.setState({
        data: [],
      });
    }
  };

  handleMetricChange = (event, newMetric) => {
    if (newMetric !== null) {
      this.setState({ metric: newMetric });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const cleanValue = String(value).replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.state.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';

    const totalClaimsCreated = rows.reduce(
      (sum, row) => sum + Number(row.claims_created || 0),
      0
    );

    const totalClaimsConsumed = rows.reduce(
      (sum, row) => sum + Number(row.claims_consumed || 0),
      0
    );

    const totalOrdersWithClaims = rows.reduce(
      (sum, row) => sum + Number(row.orders_with_claims || 0),
      0
    );

    const totalSalesFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.sales_from_claims || 0),
      0
    );

    const totalDiscountFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.discount_from_claims || 0),
      0
    );

    const weightedUsageRate = totalClaimsCreated > 0
      ? (totalClaimsConsumed / totalClaimsCreated) * 100
      : 0;

    const weightedDiscountRate = totalSalesFromClaims > 0
      ? (totalDiscountFromClaims / totalSalesFromClaims) * 100
      : 0;

    const averageDiscountPerPromoOrder = totalOrdersWithClaims > 0
      ? totalDiscountFromClaims / totalOrdersWithClaims
      : 0;

    const headers = [
      { key: 'date', label: 'Date' },
      { key: 'claims_created', label: 'Claims issued' },
      { key: 'claims_consumed', label: 'Claims used' },
      { key: 'claim_conversion', label: 'Usage %' },
      { key: 'orders_with_claims', label: 'Promo orders' },
      { key: 'sales_from_claims', label: 'Promo order value' },
      { key: 'discount_from_claims', label: 'Promo discount' },
      { key: 'discount_rate', label: 'Discount rate %' },
      { key: 'average_discount', label: 'Average discount/order' },
    ];

    const csvRows = [
      ['Promotion activity evolution'].map((value) => this.csvValue(value)).join(','),
      ['Period', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total claims issued', totalClaimsCreated].map((value) => this.csvValue(value)).join(','),
      ['Total claims used', totalClaimsConsumed].map((value) => this.csvValue(value)).join(','),
      ['Weighted usage %', `${weightedUsageRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Total promo orders', totalOrdersWithClaims].map((value) => this.csvValue(value)).join(','),
      ['Total promo order value', totalSalesFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total promo discount', totalDiscountFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted discount rate %', `${weightedDiscountRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Average discount per promo order', averageDiscountPerPromoOrder.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'claim_conversion' || header.key === 'discount_rate') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (
              header.key === 'sales_from_claims' ||
              header.key === 'discount_from_claims' ||
              header.key === 'average_discount'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `promotion_activity_evolution_${periodMode}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.state.data || [];
    const metric = this.state.metric;

    const categories = rows.map((row) => formatDate(row.date));

    let series = [
      {
        name: 'Claims issued',
        data: rows.map((row) => Number(row.claims_created || 0)),
      },
      {
        name: 'Claims used',
        data: rows.map((row) => Number(row.claims_consumed || 0)),
      },
    ];

    let yFormatter = formatNumber;
    let tooltipFormatter = formatNumber;

    if (metric === 'orders') {
      series = [
        {
          name: 'Promo orders',
          data: rows.map((row) => Number(row.orders_with_claims || 0)),
        },
        {
          name: 'Issued usage rate',
          data: rows.map((row) => Number(row.claim_conversion || 0)),
        },
      ];

      tooltipFormatter = function (value, opts) {
        if (opts && opts.seriesIndex === 1) {
          return formatPercent(value);
        }

        return formatNumber(value);
      };
    }

    if (metric === 'sales') {
      series = [
        {
          name: 'Promo order value',
          data: rows.map((row) => Number(row.sales_from_claims || 0)),
        },
        {
          name: 'Promo discount',
          data: rows.map((row) => Number(row.discount_from_claims || 0)),
        },
      ];
    }

    if (metric === 'cost') {
      series = [
        {
          name: 'Discount rate',
          data: rows.map((row) => Number(row.discount_rate || 0)),
        },
        {
          name: 'Avg. discount/order',
          data: rows.map((row) => Number(row.average_discount || 0)),
        },
      ];

      tooltipFormatter = function (value, opts) {
        if (opts && opts.seriesIndex === 0) {
          return formatPercent(value);
        }

        return formatNumber(value);
      };
    }

    const hasAnyValue = series.some((serie) =>
      (serie.data || []).some((value) => Number(value || 0) > 0)
    );

    const options = {
      chart: {
        type: 'line',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        parentHeightOffset: 0,
      },
      stroke: {
        curve: 'smooth',
        width: 2.5,
      },
      markers: {
        size: 0,
      },
      colors: ['#1a237e', '#607d8b'],
      grid: {
        borderColor: 'rgba(15, 23, 42, 0.08)',
        padding: {
          left: 8,
          right: 8,
          top: 0,
          bottom: 0,
        },
      },
      xaxis: {
        categories: categories,
        tickAmount: categories.length > 30 ? 6 : undefined,
        labels: {
          rotate: -35,
          rotateAlways: categories.length > 20,
          hideOverlappingLabels: true,
          trim: true,
          style: {
            fontSize: '11px',
          },
        },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          formatter: function (value) {
            return yFormatter(value);
          },
          style: {
            fontSize: '11px',
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (value, opts) {
            return tooltipFormatter(value, opts);
          },
        },
      },
      legend: {
        position: 'bottom',
        fontSize: '12px',
        horizontalAlign: 'center',
        markers: {
          width: 9,
          height: 9,
        },
      },
      noData: {
        text: 'No promotion activity data available',
      },
    };

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Promotion activity evolution
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.74rem', sm: '0.82rem' },
                    lineHeight: 1.25,
                  }}
                >
                  Claims, promo orders, sales impact and discount cost
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  maxWidth: '100%',
                  overflowX: { xs: 'auto', sm: 'visible' },
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadRoundedIcon />}
                  disabled={!rows.length}
                  onClick={this.handleExportCsv}
                  sx={{
                    flexShrink: 0,
                    height: 32,
                    minWidth: 64,
                    px: 1,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: '0.68rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    color: '#111827',
                    borderColor: 'rgba(15, 23, 42, 0.14)',
                    bgcolor: '#ffffff',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      borderColor: 'rgba(15, 23, 42, 0.24)',
                    },
                    '& .MuiButton-startIcon': {
                      mr: 0.5,
                      ml: 0,
                    },
                  }}
                >
                  CSV
                </Button>

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={metric}
                  onChange={this.handleMetricChange}
                  sx={{
                    flexShrink: 0,
                    bgcolor: '#fff',
                    borderRadius: '12px',
                    maxWidth: '100%',
                    '& .MuiToggleButton-root': {
                      px: { xs: 0.85, sm: 1.25 },
                      py: 0.35,
                      fontSize: { xs: '0.68rem', sm: '0.78rem' },
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      color: '#334155',
                      borderColor: 'rgba(15, 23, 42, 0.08)',
                      '&.Mui-selected': {
                        bgcolor: '#0f172a',
                        color: '#fff',
                        '&:hover': {
                          bgcolor: '#111827',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="claims">Claims</ToggleButton>
                  <ToggleButton value="orders">Orders</ToggleButton>
                  <ToggleButton value="sales">Sales</ToggleButton>
                  <ToggleButton value="cost">Cost</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <Chart
                options={options}
                series={series}
                type="line"
                height="100%"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsEvolution;
